import { forwardRef } from "react"
import { IMaskInput } from "react-imask"

import dayjs from "dayjs"

export const TelephoneMask = forwardRef(function PhoneMask(props, ref) {
    const { onChange, ...other } = props

    return (
        <IMaskInput
            {...other}
            mask={[
                { mask: "(00) 0000-0000" },
                { mask: "(00) 00000-0000" }
            ]}
            inputRef={ref}
            overwrite={true}
            onAccept={(value) => onChange({ target: { value } })}
        />
    )
})

export const formatBRL = (value) => {
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(value)
}

export const isValidEmail = (value = "") => {
    const normalizedValue = String(value).trim()

    if (!/^[^\s@]+@[^\s@]+$/.test(normalizedValue))
        return false

    const [localPart, domain] = normalizedValue.split("@")

    if (!localPart || localPart.startsWith(".") || localPart.endsWith(".") || localPart.includes("..") || !domain || domain.includes(".."))
        return false

    const domainLabels = domain.split(".")

    return domainLabels.length > 1 && domainLabels.every((label) => /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(label))
}

export const getPhoneDigits = (phone) => String(phone || "").replace(/\D/g, "")

const BRAZILIAN_DDDS = [
    "11", "12", "13", "14", "15", "16", "17", "18", "19",
    "21", "22", "24",
    "27", "28",
    "31", "32", "33", "34", "35", "37", "38",
    "41", "42", "43", "44", "45", "46",
    "47", "48", "49",
    "51", "53", "54", "55",
    "61",
    "62", "64",
    "63",
    "65", "66",
    "67",
    "68",
    "69",
    "71", "73", "74", "75", "77",
    "79",
    "81", "87",
    "82",
    "83",
    "84",
    "85", "88",
    "86", "89",
    "91", "93", "94",
    "92", "97",
    "95",
    "96",
    "98", "99",
]

export const isValidBrazilianPhone = (phone, countryCode = "BR") => {
    if (countryCode !== "BR")
        return true

    const digits = getPhoneDigits(phone)

    if (!digits.startsWith("55"))
        return false

    const ddd = digits.slice(2, 4)
    const localNumber = digits.slice(4)

    if (!BRAZILIAN_DDDS.includes(ddd))
        return false

    return localNumber.startsWith("9") ? localNumber.length === 9 : localNumber.length === 8
}

export const formatTelephone = (value) => {
    if (! value)
        return
    
    const digits = value.replace(/\D/g, '')

    const raw = digits.startsWith('55') ? digits.slice(2) : digits

    const areaCode = raw.slice(0, 2)
    const number = raw.slice(2)

    if (number.length === 9)
        return `(${areaCode}) ${number.slice(0, 5)}-${number.slice(5)}`
    else if (number.length === 8)
        return `(${areaCode}) ${number.slice(0, 4)}-${number.slice(4)}`
    else
        return value
}

export const isValidCPF = (cpf) => {
    cpf = cpf.replace(/\D/g, "")
    if (cpf.length !== 11)
        return false

    if (/^(\d)\1+$/.test(cpf))
        return false

    let sum = 0
    for (let i = 0; i < 9; i++)
        sum += parseInt(cpf[i]) * (10 - i)

    let firstDigit = (sum * 10) % 11
    if (firstDigit === 10) firstDigit = 0

    if (firstDigit !== parseInt(cpf[9]))
        return false

    sum = 0
    for (let i = 0; i < 10; i++)
        sum += parseInt(cpf[i]) * (11 - i)

    let secondDigit = (sum * 10) % 11
    if (secondDigit === 10) secondDigit = 0

    return secondDigit === parseInt(cpf[10])
}

export const normalizeCNPJ = (cnpj = "") => String(cnpj).toUpperCase().replace(/[^0-9A-Z]/g, "")

export const isValidCNPJ = (cnpj) => {
    const rawCNPJ = String(cnpj || "").toUpperCase()

    if (/[^0-9A-Z.\-/\s]/.test(rawCNPJ))
        return false

    cnpj = normalizeCNPJ(rawCNPJ)

    if (cnpj.length !== 14)
        return false

    if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj))
        return false

    if (/^([0-9A-Z])\1+$/.test(cnpj))
        return false

    const calcCheckDigit = (base, weights) => {
        const sum = base
            .split("")
            .reduce((acc, char, i) => acc + (char.charCodeAt(0) - 48) * weights[i], 0)

        const rest = sum % 11
        return rest < 2 ? 0 : 11 - rest
    }

    const base = cnpj.slice(0, 12)
    const digit1 = calcCheckDigit(base, [5,4,3,2,9,8,7,6,5,4,3,2])
    const digit2 = calcCheckDigit(base + digit1, [6,5,4,3,2,9,8,7,6,5,4,3,2])

    return cnpj === `${base}${digit1}${digit2}`
}

export const getBillingCycleProgress = (enterprise) => {
    if (!enterprise?.start_charging) {
        return 0
    }

    const startCharging = dayjs(enterprise.start_charging).startOf("day")
    const today = dayjs().startOf("day")

    if (!startCharging.isValid()) return 0

    const billingDay = startCharging.date()

    let cycleStart = today.date(billingDay).startOf("day")
    if (cycleStart.isAfter(today)) {
        cycleStart = cycleStart.subtract(1, "month")
    }

    let cycleEnd = cycleStart.add(1, "month")

    const totalMs = cycleEnd.diff(cycleStart, "millisecond")
    const elapsedMs = today.diff(cycleStart, "millisecond")

    let progress = (elapsedMs / totalMs) * 100

    progress = Math.max(0, Math.min(100, progress))

    return Math.round(progress)
}

export const getRenewalDate = (enterprise) => {
    if (!enterprise?.start_charging) {
        return enterprise?.end_charging ?? null
    }

    const startCharging = dayjs(enterprise.start_charging).startOf("day")

    if (!startCharging.isValid()) {
        return enterprise?.end_charging ?? null
    }

    const today = dayjs().startOf("day")
    const billingDay = startCharging.date()

    let nextRenewal = today.date(billingDay).startOf("day");
    if (nextRenewal.isBefore(today) || nextRenewal.isSame(today)) {
        nextRenewal = nextRenewal.add(1, "month")
    }

    return nextRenewal.toDate()
}

export const getGreetings = () => {
    const hour = new Date().getHours()
    if (hour >= 5 && hour < 12)
        return "bom dia"
    else if (hour >= 12 && hour < 18)
        return "boa tarde"
    else
        return "boa noite"
}

export const isValidBirthday = (value) => {
    if (!value) {
        return true
    }

    const date = new Date(`${value}T00:00:00`)

    if (Number.isNaN(date.getTime())) {
        return false
    }

    const [year, month, day] = value.split('-').map(Number)

    if (
        date.getFullYear() !== year ||
        date.getMonth() + 1 !== month ||
        date.getDate() !== day
    ) {
        return false
    }

    if (year < 1900) {
        return false
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return date <= today
}
